import type {
  PDFReader,
  PDFReaderOptions,
  PDFWriter,
} from "@pdfking/renderer-api"
import type { Bytes } from "@pdfking/tfunc"
import {
  BlobToUint8,
  First,
  LockManager,
  ResizeBlobs,
  Sleep,
  StringToUInt8,
} from "@pdfking/tfunc"

import { CopyPage } from "../functions/CopyPage"
import { ExtractPage } from "../functions/ExtractPage"
import { GetLastError } from "../functions/GetLastError"
import { InitializePdfium } from "../functions/InitializePdfium"
import { RenderPage } from "../functions/RenderPage"
import { SaveDocument } from "../functions/SaveDocument"

//import AwaitLock from "await-lock"
//import get_size_for_response from "@pdfking/workflow/requests/get_size_for_response"
//import get_content_type_for_response from "@pdfking/workflow/requests/get_content_type_for_response"
//import make_cache from "@pdfking/workflow/storage/caches/document_cache"
//import chain_promises from "@pdfking/workflow/utils/chain_promises"
//import is_blob_url from "@pdfking/workflow/utils/is_blob_url"

const pdfium_lock = new LockManager()
const schedule = <T>(func: () => Promise<T>) => pdfium_lock.schedule(30n, func)
const schedule_iterable = <T>(stream: AsyncGenerator<T>) =>
  pdfium_lock.schedule_iterable(30n, stream)

// GENERAL CONSTANTS

// PDFium constants
const PDF_DATA_NOTAVAIL = 0
const PDF_DATA_AVAIL = 1

// Create download promise for a chunk

// Check if data is available in blobs array
const is_data_available = (blobs_array, chunk_size, offset, size) => {
  const start_chunk = Math.floor(offset / chunk_size)
  const end_chunk = Math.floor((offset + size - 1) / chunk_size)

  for (let i = start_chunk; i <= end_chunk; i++) {
    if (blobs_array[i] == null) {
      return false
    }
  }
  return true
}

// Get data from blobs array (synchronous using blob URLs and sync XHR)
const get_data_from_blobs = (blobs_array, chunk_size, offset, size) => {
  const result = new Uint8Array(size)
  let result_offset = 0

  const start_chunk = Math.floor(offset / chunk_size)
  const end_chunk = Math.floor((offset + size - 1) / chunk_size)
  const start_offset = offset % chunk_size

  for (let i = start_chunk; i <= end_chunk; i++) {
    const chunk_blob = blobs_array[i]
    if (!chunk_blob) {
      throw new Error(`Chunk ${i} not in blobs array`)
    }

    // Calculate slice boundaries for this chunk
    let chunk_start = i === start_chunk ? start_offset : 0
    let chunk_end = chunk_blob.size

    if (i === end_chunk) {
      const remaining = size - result_offset
      chunk_end = Math.min(chunk_start + remaining, chunk_blob.size)
    }

    // Slice the blob to get only the needed portion
    const url = URL.createObjectURL(chunk_blob.slice(chunk_start, chunk_end))

    try {
      const xhr = new XMLHttpRequest()
      xhr.open("GET", url, false) // synchronous

      // Use overrideMimeType to ensure binary data handling in all environments
      if (xhr.overrideMimeType) {
        xhr.overrideMimeType("text/plain; charset=x-user-defined")
      }

      xhr.send()

      // Convert response to binary data - works in both browser and Puppeteer
      const response = xhr.response
      const chunk_data = new Uint8Array(response.length)
      for (let j = 0; j < response.length; j++) {
        chunk_data[j] = response.charCodeAt(j) & 0xff
      }

      result.set(chunk_data, result_offset)
      result_offset += chunk_data.length
    } finally {
      // Always revoke the blob URL to prevent memory leaks
      URL.revokeObjectURL(url)
    }
  }

  return result
}

const load_router = async (options: PDFReaderOptions, pdfium: any) => {
  const { stream, size, password = "" } = options

  // Resize incoming stream to optimal chunk size for PDFium (default 30MB)
  const chunk_size = (1024 * 1024 * 30) as Bytes
  const resized_stream = ResizeBlobs(stream, chunk_size)
  const blobs_array = {}
  let chunk_index = 0

  // Set up password
  const password_byte_size = StringToUInt8(password).length + 1
  const password_pointer = pdfium.pdfium.wasmExports.malloc(password_byte_size)
  pdfium.pdfium.stringToUTF8Helper(
    password,
    password_pointer,
    password_byte_size
  )

  const cleanup_functions = []
  cleanup_functions.push(() => pdfium.pdfium.wasmExports.free(password_pointer))

  const blob_generator = async function* () {
    for await (const blob of resized_stream) {
      blobs_array[chunk_index] = blob
      chunk_index++
      yield blob
    }
  }

  // Create both arrays upfront

  const promise_generator = blob_generator()

  // Check if single chunk - load directly into memory
  if (size <= chunk_size) {
    // Get the single blob
    const blob = await First(promise_generator)

    // Convert blob to array buffer
    const uint8Array = await BlobToUint8(blob)

    // Allocate memory in WASM
    const data_pointer = pdfium.pdfium.wasmExports.malloc(uint8Array.length)
    cleanup_functions.push(() => pdfium.pdfium.wasmExports.free(data_pointer))

    // Copy data to WASM memory
    pdfium.pdfium.HEAPU8.set(uint8Array, data_pointer)

    // Load document from memory
    const doc_pointer = pdfium.LoadMemDocument(
      data_pointer,
      uint8Array.length,
      password_pointer
    )

    return {
      doc_pointer,
      cleanup_functions,
      blobs_array,
      size,
      file_pointer: null,
      avail: null,
      hints_ptr: null,
      promise_generator: null,
    }
  }

  // Create FX_FILEAVAIL structure
  const isDataAvail = (_pThis, offset, size) =>
    is_data_available(blobs_array, chunk_size, offset, size) ? 1 : 0

  const isDataAvail_ptr = pdfium.pdfium.addFunction(isDataAvail, "iiii")
  cleanup_functions.push(() => pdfium.pdfium.removeFunction(isDataAvail_ptr))

  const file_avail_ptr = pdfium.pdfium.wasmExports.malloc(8)
  cleanup_functions.push(() => pdfium.pdfium.wasmExports.free(file_avail_ptr))

  pdfium.pdfium.setValue(file_avail_ptr, 1, "i32") // version
  pdfium.pdfium.setValue(file_avail_ptr + 4, isDataAvail_ptr, "i32")

  // Create FX_DOWNLOADHINTS structure
  const addSegment = (_pThis, offset, size) => {
    // Promises are already created, this is just a hint that data is needed
    // PDFium will call getBlock when it actually needs the data
  }

  const addSegment_ptr = pdfium.pdfium.addFunction(addSegment, "viii")
  cleanup_functions.push(() => pdfium.pdfium.removeFunction(addSegment_ptr))

  const hints_ptr = pdfium.pdfium.wasmExports.malloc(8)
  cleanup_functions.push(() => pdfium.pdfium.wasmExports.free(hints_ptr))

  pdfium.pdfium.setValue(hints_ptr, 1, "i32") // version
  pdfium.pdfium.setValue(hints_ptr + 4, addSegment_ptr, "i32")

  // Create FPDF_FILEACCESS structure
  const getBlock = (_param, position, pBuf, size) => {
    if (!is_data_available(blobs_array, chunk_size, position, size)) {
      return 0
    }

    const dest = new Uint8Array(pdfium.pdfium.HEAPU8.buffer, pBuf, size)
    dest.set(get_data_from_blobs(blobs_array, chunk_size, position, size))
    return 1
  }

  const getBlock_ptr = pdfium.pdfium.addFunction(getBlock, "iiiii")
  cleanup_functions.push(() => pdfium.pdfium.removeFunction(getBlock_ptr))

  const file_access_ptr = pdfium.pdfium.wasmExports.malloc(12)
  cleanup_functions.push(() => pdfium.pdfium.wasmExports.free(file_access_ptr))

  pdfium.pdfium.setValue(file_access_ptr, size, "i32") // m_FileLen
  pdfium.pdfium.setValue(file_access_ptr + 4, getBlock_ptr, "i32") // m_GetBlock
  pdfium.pdfium.setValue(file_access_ptr + 8, 0, "i32") // m_Param

  // Create availability provider
  const avail = pdfium.AvailCreate(file_avail_ptr, file_access_ptr)
  cleanup_functions.push(() => pdfium.AvailDestroy(avail))

  // Progressive loading loop
  let doc_pointer = null
  let avail_done = false

  while (!avail_done) {
    // Check document availability
    const doc_status = pdfium.AvailIsDocAvail(avail, hints_ptr)

    if (doc_status === PDF_DATA_AVAIL) {
      // Document is ready
      // console.log("😘 DOCUMENT READY")
      avail_done = true
    } else if (doc_status === PDF_DATA_NOTAVAIL) {
      // console.log("🫥 DOCUMENT NOT READY", doc_status)
      // CheckLinearization(pdfium, avail)

      // Wait for any pending chunk downloads
      const { done } = await promise_generator.next()

      avail_done = done
    } else {
      // Error
      throw new Error(`Document loading error: status ${doc_status} source`)
    }
  }

  doc_pointer = pdfium.AvailGetDocument(avail, password_pointer)

  return {
    doc_pointer,
    file_pointer: file_access_ptr,
    cleanup_functions,
    avail,
    hints_ptr,
    blobs_array,
    promise_generator,
    size,
  }
}

export const CreatePDFReader = async ({
  stream,
  size,
  password = "",
}: PDFReaderOptions): Promise<PDFReader> => {
  const pdfium = await schedule(InitializePdfium)

  const result = await schedule(() =>
    load_router({ stream, size, password }, pdfium)
  )

  const close = async () => {
    if (result.doc_pointer) {
      // @ts-expect-error - pdfium methods not typed
      await schedule(async () => pdfium.CloseDocument(result.doc_pointer))
    }

    for (const func of result.cleanup_functions || []) {
      func()
    }
  }

  const is_page_available = async page => {
    // If no avail interface (single chunk), all pages are available
    if (!result.avail) {
      return true
    }

    const status = await schedule(async () =>
      // @ts-expect-error - pdfium methods not typed
      pdfium.AvailIsPageAvail(result.avail, page - 1, result.hints_ptr)
    )

    return status === PDF_DATA_AVAIL
  }

  const ensure_page = async (page: number, delay = 50) => {
    while (true) {
      if (await is_page_available(page)) {
        return page
      } else {
        const { done } = await result.promise_generator.next()

        if (done && !(await is_page_available(page))) {
          await close()
          throw new Error(`Page ${page} not available after maximum attempts`)
        }
      }
      await Sleep(delay)
    }
  }

  const error = await schedule(async () => GetLastError(pdfium))

  if (error) console.error(error)

  const internal_page_count = error
    ? 0
    : // @ts-expect-error - pdfium methods not typed
      await schedule(async () => pdfium.GetPageCount(result.doc_pointer))

  const res: { doc_pointer: number; pdfium: any } & PDFReader = {
    close,
    doc_pointer: result.doc_pointer,
    pdfium,

    async page_count() {
      return internal_page_count
    },

    async extract_page_as_pdf(index: number) {
      await ensure_page(index)
      return await schedule(async () =>
        ExtractPage(
          { ...result, pdfium, doc_pointer: result.doc_pointer },
          index
        )
      )
    },

    async rasterize(
      index: number,
      options?: { scale?: number; max_size?: number }
    ) {
      await ensure_page(index)
      return await schedule(async () =>
        RenderPage(
          { ...result, pdfium, doc_pointer: result.doc_pointer },
          index,
          options
        )
      )
    },
  }

  return res
}

export const CreatePDFWriter = async (): Promise<PDFWriter> => {
  const pdfium = await schedule(InitializePdfium)

  const doc_pointer: number = await schedule(async () =>
    // @ts-expect-error - pdfium methods not typed
    pdfium.CreateNewDocument()
  )

  if (!doc_pointer) {
    const error = await schedule(async () => GetLastError(pdfium))
    throw new Error(`Failed to create new PDF document: ${error}`)
  }

  const close = async () => {
    if (doc_pointer) {
      // @ts-expect-error - pdfium methods not typed
      await schedule(async () => pdfium.CloseDocument(doc_pointer))
    }
  }

  return {
    async insert(source: PDFReader, insert_at: number): Promise<void> {
      // Source should be a single-page PDF
      const pageCount = await source.page_count()
      if (pageCount !== 1) {
        throw new Error(`Expected source to have 1 page, got ${pageCount}`)
      }

      // Use CopyPage to import all pages from source reader
      await schedule(async () =>
        CopyPage(
          {
            pdfium: (source as any).pdfium,
            doc_pointer: (source as any).doc_pointer,
          },
          { pdfium, doc_pointer },
          insert_at
        )
      )
    },

    save(): AsyncGenerator<Blob> {
      return schedule_iterable(SaveDocument({ pdfium, doc_pointer }))
    },

    close,
  }
}
